import React from "react";
import { Box } from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import { useLandingTheme } from "../../LandingThemeContext";
import { DappledLight } from "../../visuals/DappledLight";
import { JungleHeroContent } from "./JungleHeroContent";

// Fractal noise tile for the hero's film grain — generated by the browser, no asset to load.
const GRAIN_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="180" height="180"><filter id="g"><feTurbulence type="fractalNoise" baseFrequency="0.95" numOctaves="2" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 0.5 0 0 0 0 0.5 0 0 0 0 0.5 0 0 0 1 0"/></filter><rect width="180" height="180" filter="url(#g)"/></svg>',
  );

// The forest floor: a photographed moss bed under moving canopy light, with a real lecture slide
// and the Jungle question it became. Headline and upload controls only.
export function MossHero() {
  const theme = useLandingTheme();
  const reduceMotion = useReducedMotion();
  return (
    <Box
      as="section"
      position="relative"
      overflow="hidden"
      // Runs up behind the transparent nav so the photograph owns the whole fold.
      mt="-96px"
      pt={{ base: "128px", lg: "168px" }}
      pb={{ base: "72px", lg: "112px" }}
      minH={{ lg: "860px" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      bg={theme.color.accentDark}
    >
      {/* Photograph, blurred into soft green tone so the type and the card carry the fold. Oversized so the
          blur has no faded edges. */}
      <Box aria-hidden="true" position="absolute" inset="-60px" bgImage="url(/images/landing/generated/moss-forest-floor.webp)" bgSize="cover" bgPosition="center" filter="blur(30px)" />
      {/* Film grain: a tiled fractal-noise SVG, faint and overlaid, so the blurred photograph reads
          as a surface rather than a flat gradient. */}
      <Box aria-hidden="true" position="absolute" inset={0} opacity={0.18} mixBlendMode="overlay" bgImage={`url("${GRAIN_SVG}")`} bgSize="180px 180px" />
      {/* Legibility: deep moss shadow behind the type, clear over the card */}
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bgImage="linear-gradient(100deg, rgba(20, 42, 16, 0.92) 0%, rgba(20, 42, 16, 0.78) 38%, rgba(20, 42, 16, 0.25) 62%, rgba(20, 42, 16, 0.15) 100%), linear-gradient(to top, rgba(20, 42, 16, 0.9) 0%, rgba(20, 42, 16, 0) 40%)"
      />
      <DappledLight animate={!reduceMotion} opacity={0.8} />

      <JungleHeroContent onDark />
    </Box>
  );
}
